import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { axiosInstance } from '@/App';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

function PaymentSuccess({ setUser }) {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [creditsAdded, setCreditsAdded] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      checkPaymentStatus(sessionId);
    } else {
      setStatus('error');
      toast.error('Invalid payment session');
    }
  }, [searchParams]);

  const checkPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000; // 2 seconds

    try {
      const response = await axiosInstance.get(`/payments/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setStatus('success');
        setCreditsAdded(response.data.credits_added);
        toast.success(`Payment successful! ${response.data.credits_added} credits added to your account.`);
        
        // Refresh user data
        const userResponse = await axiosInstance.get('/auth/me');
        setUser(userResponse.data);
        return;
      } else if (response.data.status === 'expired') {
        setStatus('error');
        toast.error('Payment session expired');
        return;
      }

      // If payment is still pending and we haven't reached max attempts, poll again
      if (attempts < maxAttempts) {
        setTimeout(() => checkPaymentStatus(sessionId, attempts + 1), pollInterval);
      } else {
        setStatus('error');
        toast.error('Payment verification timeout. Please check your dashboard.');
      }
    } catch (error) {
      console.error('Payment status check error:', error);
      setStatus('error');
      toast.error('Failed to verify payment status');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-beige-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
        {status === 'checking' && (
          <>
            <div className="w-16 h-16 border-4 border-forest-green border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Processing Payment...</h2>
            <p className="text-gray-600">Please wait while we confirm your payment.</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Payment Successful!</h2>
            <p className="text-gray-600 mb-6">
              {creditsAdded} alert {creditsAdded === 1 ? 'credit has' : 'credits have'} been added to your account.
            </p>
            <Button
              onClick={() => navigate('/dashboard')}
              className="w-full bg-forest-green hover:bg-forest-green-dark"
              size="lg"
            >
              Go to Dashboard
            </Button>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">Payment Verification Failed</h2>
            <p className="text-gray-600 mb-6">
              We couldn't verify your payment. Please check your dashboard or contact support.
            </p>
            <Button
              onClick={() => navigate('/dashboard')}
              className="w-full bg-forest-green hover:bg-forest-green-dark"
              size="lg"
            >
              Go to Dashboard
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

export default PaymentSuccess;
